import React from 'react';
import { ReactSVG } from 'react-svg';
import { getContext } from 'common';
import _merge from 'lodash/merge';
import Styled from './styles';

const cleanUpSvg = string => string.replace(/<title>[a-z0-9_#]*<\/title>/gim, '');

export interface IconProps extends React.ComponentPropsWithRef<'div'> {
  /** Svg string eg. <svg>...</svg> (useful if you are trying to animate particular paths/shapes in your svg) */
  children?: string | React.ReactNode;
  /** URL of Icon (alternatively you can also use the 'children' prop to pass in an SVG.) Icons ending in `_ltr` will not be flipped when switching to right-to-left context. */
  src?: string;
  /** Size of the Icon eg. 100px */
  size?: string | number | {
    height?: string | number,
    width?: string | number
  };
  /** Color of the icon */
  color?: string;
  /** Retain icon's original color */
  retainColor?: boolean;
  /**
   * Optional properties that ReactSVG may offer. For example, loading or fallback properties
   * to display something during those states. See ReactSVG for more information. https://www.npmjs.com/package/react-svg
   */
  reactSVGProps?: Record<string, any>;
  /** @ignore Aria label (Icon description used for accessibility) */
  ariaLabel?: string;
  /** Allow Right-to-left flipping of the icon */
  RTL?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** @ignore rtl variant of this icon, the value of this prop is generated (in CCFK-assets repo) */
  rtlSVG?: React.ReactNode;
}

/** Component for displaying custom SVG icons. For internal CCFK icons. Please see "icon list" story for a list of available icons. All internal icons share the same "Icon" properties (listed in the documentation below).  */
const Icon = React.forwardRef<HTMLElement, IconProps>((props: IconProps, ref) =>
  getContext(({ RTL: globalRTL, theme: globalTheme }) => {
    const {
      children, color, ariaLabel, src, rtlSVG, theme, reactSVGProps, RTL, ...otherProps
    } = props;

    const isSvgString = s => !src && typeof s === 'string';
    // check if src is LTR only (appended with _ltr)
    const isSrcLtrOnly = isSvgString(src) && /[ \w-]+?_ltr\.svg/.test(src);
    const isRTL = globalRTL && RTL && !isSrcLtrOnly;
    /* eslint-disable-next-line react/no-danger */
    const renderString = string => isSvgString(string) && <div dangerouslySetInnerHTML={{ __html: string }} />;
    const resolvedTheme = _merge({}, globalTheme.ICON, theme);
    const renderChildren = () => {
      // if rtl and has rtlSVG variant
      if (isRTL && rtlSVG) {
        return renderString(cleanUpSvg(rtlSVG));
      }
      // if child is a string, clean up and render
      if (isSvgString(children)) {
        return renderString(cleanUpSvg(children));
      }
      return React.isValidElement(children) && children;
    };
    return (
      <Styled.Icon
        aria-label={ariaLabel}
        role="img"
        ref={ref}
        $color={color}
        $theme={resolvedTheme}
        RTL={isRTL}
        rtlSVG={rtlSVG}
        {...otherProps}
      >
        <Styled.IconSvgWrapper aria-hidden="true" size={props.size}>
          {renderChildren()}
          {src && (
            <ReactSVG
              src={src}
              afterInjection={(error) => {
                // eslint-disable-next-line no-console
                if (error) console.error(`Icon component ReactSVG error: ${error}`);
              }}
              beforeInjection={(svg) => {
                // remove embedded title used for default tooltip
                // @ts-expect-error: review the following line
                const [theTitle] = [...svg.querySelectorAll('title')];
                theTitle !== undefined && svg.removeChild(theTitle);
              }}
              {...reactSVGProps}
            />
          )}
        </Styled.IconSvgWrapper>
      </Styled.Icon>
    );
  }));

Icon.defaultProps = {
  src: undefined,
  rtlSVG: undefined,
  children: undefined,
  reactSVGProps: undefined,
  ariaLabel: 'icon',
  RTL: true,
  color: undefined,
  retainColor: false,
  size: '1.5rem',
  theme: undefined
};
Icon.displayName = 'Icon';
export default Icon;
